/**
 * Identifiants des Apps développeur (Meta, LinkedIn, TikTok, Google) stockés en
 * base (`social_app_configs`, migration 0132) au lieu des variables du serveur.
 * Le secret (App/Client Secret) est write-only → Vault, jamais relu.
 * Utilisé par « Paramètres de connexion » et « Accès réseaux sociaux » (blog).
 *
 * Mobile-first : champs pleine largeur, cibles ≥ 44 px.
 */

import { useEffect, useState } from 'react';
import { Save, Copy, Check, CheckCircle2, Facebook, Linkedin, Video, Youtube } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { useSocialConfigs, useUpdateSocialConfig } from '@/lib/admin/use-social';
import type { SocialAppConfig, SocialConfigPatch, SocialProvider } from '@/lib/admin/social-client';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';

/** URL publique de l'edge fn social-oauth-callback (à enregistrer chez Meta/LinkedIn). */
function callbackUrl(): string {
  const supa = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
  if (!supa) return '';
  return `${supa.replace(/\/$/, '').replace('.supabase.co', '.functions.supabase.co')}/social-oauth-callback`;
}

interface FieldDef {
  key: keyof SocialConfigPatch;
  label: string;
  placeholder?: string;
  optional?: boolean;
}

const PROVIDER_FIELDS: Record<SocialProvider, FieldDef[]> = {
  meta: [
    { key: 'client_id', label: 'App ID', placeholder: 'ex. 1234567890' },
    { key: 'page_id', label: 'Page ID', placeholder: 'id de la Page Facebook', optional: true },
  ],
  linkedin: [
    { key: 'client_id', label: 'Client ID' },
    { key: 'org_id', label: 'Organization ID', placeholder: 'id de la page entreprise' },
    { key: 'api_version', label: "Version d'API", placeholder: '202405', optional: true },
  ],
  tiktok: [{ key: 'client_id', label: 'Client key', placeholder: 'ex. awxyz...' }],
  google: [
    { key: 'client_id', label: 'Client ID', placeholder: 'xxxxx.apps.googleusercontent.com' },
  ],
};

/** Ce que le serveur a en variables (repli quand la base n'a rien). */
export type AppEnvStatus = Partial<Record<SocialProvider, { clientId: boolean; secret: boolean }>>;

const ENV_NAMES: Record<SocialProvider, string> = {
  meta: 'META_APP_ID / META_APP_SECRET',
  linkedin: 'LINKEDIN_CLIENT_ID / LINKEDIN_CLIENT_SECRET',
  tiktok: 'TIKTOK_CLIENT_KEY / TIKTOK_CLIENT_SECRET',
  google: 'GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET',
};

/** Les quatre Apps, avec l'URL de redirection à enregistrer chez chacune. */
export function SocialAppConfigsSection({ envStatus }: { envStatus?: AppEnvStatus }) {
  const { data: configs = [], isLoading } = useSocialConfigs();
  const byProvider = new Map(configs.map((c) => [c.provider, c]));
  const cb = callbackUrl();
  // Google/YouTube a son propre callback (youtube-oauth-callback), distinct du
  // callback réseaux (social-oauth-callback) utilisé par Meta/LinkedIn/TikTok.
  const ytCb = cb ? cb.replace('social-oauth-callback', 'youtube-oauth-callback') : '';
  const cards: {
    provider: SocialProvider;
    label: string;
    Icon: typeof Facebook;
    redirect: string;
  }[] = [
    { provider: 'meta', label: 'Meta (Facebook + Instagram)', Icon: Facebook, redirect: cb },
    { provider: 'linkedin', label: 'LinkedIn (page entreprise)', Icon: Linkedin, redirect: cb },
    { provider: 'tiktok', label: 'TikTok', Icon: Video, redirect: cb },
    { provider: 'google', label: 'Google / YouTube (Shorts)', Icon: Youtube, redirect: ytCb },
  ];

  return (
    <>
      <CallbackBox url={cb} />
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="border-primary-500 h-7 w-7 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-4">
          {cards.map((c) => {
            const config = byProvider.get(c.provider);
            const env = envStatus?.[c.provider];
            return (
              <ProviderCard
                key={c.provider}
                provider={c.provider}
                label={c.label}
                Icon={c.Icon}
                config={config}
                defaultRedirect={c.redirect}
                note={
                  !config?.client_id && env?.clientId
                    ? `Pas encore en base : le serveur utilise ses variables (${ENV_NAMES[c.provider]}). Les saisir ici les remplace.`
                    : null
                }
              />
            );
          })}
        </div>
      )}
    </>
  );
}

function CallbackBox({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard indisponible */
    }
  }
  return (
    <div className="border-primary-200 bg-primary-50 mb-5 rounded-2xl border p-4">
      <p className="font-display text-ink-700 text-[12px] font-semibold">
        URL de redirection (Redirect URI) à enregistrer dans les Apps Meta, LinkedIn et TikTok
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="text-ink-900 min-w-0 flex-1 truncate rounded-lg bg-white px-2.5 py-2 font-mono text-[12px]">
          {url}
        </code>
        <button
          type="button"
          onClick={copy}
          className="border-ink-200 text-ink-700 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border bg-white px-3 font-sans text-[12px] font-semibold active:scale-[0.98]"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
    </div>
  );
}

function buildForm(config: SocialAppConfig | undefined, defaultRedirect: string) {
  return {
    client_id: config?.client_id ?? '',
    page_id: config?.page_id ?? '',
    org_id: config?.org_id ?? '',
    api_version: config?.api_version ?? '',
    redirect_uri: config?.redirect_uri ?? defaultRedirect,
    secret: '',
  };
}

function ProviderCard({
  provider,
  label,
  Icon,
  config,
  defaultRedirect,
  note,
}: {
  provider: SocialProvider;
  label: string;
  Icon: typeof Facebook;
  config: SocialAppConfig | undefined;
  defaultRedirect: string;
  note?: string | null;
}) {
  const update = useUpdateSocialConfig();
  const [form, setForm] = useState(() => buildForm(config, defaultRedirect));

  // Re-synchronise après chargement / sauvegarde (efface le champ secret).
  useEffect(() => {
    setForm(buildForm(config, defaultRedirect));
  }, [config?.updated_at, config?.secret_set, defaultRedirect]);

  const fields = PROVIDER_FIELDS[provider];
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    const patch: SocialConfigPatch = {
      client_id: form.client_id,
      redirect_uri: form.redirect_uri,
    };
    if (provider === 'meta') patch.page_id = form.page_id;
    if (provider === 'linkedin') {
      patch.org_id = form.org_id;
      patch.api_version = form.api_version;
    }
    if (form.secret.trim()) patch.secret = form.secret.trim();
    await update.mutateAsync({ provider, patch });
  }

  return (
    <section className="border-ink-200 rounded-2xl border bg-white p-4 sm:p-5">
      <h2 className="font-display text-ink-900 mb-3 inline-flex items-center gap-2 text-[15px] font-extrabold">
        <Icon className="h-4 w-4" />
        {label}
      </h2>
      {note && <p className="text-ink-500 -mt-1 mb-3 font-sans text-[12px]">{note}</p>}

      <div className="space-y-3">
        {fields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1.5">
            <label className={labelCls}>
              {field.label}
              {field.optional && <span className="text-ink-400 font-normal"> (optionnel)</span>}
            </label>
            <input
              className={inputCls}
              placeholder={field.placeholder}
              value={(form[field.key as keyof typeof form] as string) ?? ''}
              onChange={(e) => set(field.key, e.target.value)}
            />
          </div>
        ))}

        {/* Secret — write-only */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            {provider === 'meta' ? 'App Secret' : 'Client Secret'}
            {config?.secret_set && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> configuré
              </span>
            )}
          </label>
          <input
            type="password"
            autoComplete="new-password"
            className={inputCls}
            placeholder={
              config?.secret_set ? '•••••••• (laisser vide pour ne pas changer)' : 'Secret'
            }
            value={form.secret}
            onChange={(e) => set('secret', e.target.value)}
          />
        </div>

        {/* Redirect URI */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Redirect URI</label>
          <input
            className={inputCls}
            value={form.redirect_uri}
            onChange={(e) => set('redirect_uri', e.target.value)}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Button className="flex items-center gap-1.5" disabled={update.isPending} onClick={save}>
          <Save className="h-4 w-4" />
          {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
        {update.isSuccess && !update.isPending && (
          <span className="inline-flex items-center gap-1 font-sans text-[12px] font-semibold text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Enregistré
          </span>
        )}
        {update.isError && (
          <span className="text-accent-600 font-sans text-[12px]">{update.error?.message}</span>
        )}
      </div>
    </section>
  );
}
